import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject
} from '@angular/core';
import { SelectItem, PrimeTemplate } from 'primeng/api';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import Dynamsoft from 'dwt';
import { Bind } from 'primeng/bind';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Panel } from 'primeng/panel';
import { Button } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';

interface DropdownOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-web-scanning-dialog',
  templateUrl: './web-scanning-dialog.component.html',
  styleUrls: ['./web-scanning-dialog.component.scss'],
  imports: [
    Bind,
    Dialog,
    Select,
    FormsModule,
    ToggleSwitch,
    Panel,
    PrimeTemplate,
    Button,
    ProgressSpinner,
    TranslatePipe
  ]
})
export class WebScanningDialogComponent implements OnChanges, OnDestroy {
  private readonly translate = inject(TranslateService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() filesScanned = new EventEmitter<File[]>();

  private static readonly DEFAULT_VIEW_MODE = 3;
  private static readonly BLANK_PAGE_THRESHOLD = 10;
  private static readonly INIT_TIMEOUT = 10000;

  isLoading = false;
  DWTObject: any = null;
  eachPageAsDocument = false;
  splitAtBlankPage: boolean = false;

  sources: string[] = [];
  selectedSource = '';

  scanSettings = {
    colorDepth: 1,
    dpi: 300,
    feederEnabled: true
  };
  colorDepthOptions: DropdownOption[] = [];
  dpiOptions: DropdownOption[] = [];

  constructor() {
    this.initializeOptions();
  }

  /**
   * Initializes dropdown options for color depth and DPI settings.
   */
  private initializeOptions() {
    this.colorDepthOptions = [
      { label: this.translate.instant('webScanning.grayscale'), value: 1 },
      { label: this.translate.instant('webScanning.color'), value: 2 }
    ];

    this.dpiOptions = [100, 150, 200, 300].map(dpi => ({
      label: dpi.toString(),
      value: dpi
    }));
  }

  /**
   * Called when input properties change.
   * @param changes Object containing the changed input properties.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue === true) {
      this.isLoading = true;
      this.initDynamsoft().catch(error => {
        console.error('Failed to initialize Dynamsoft:', error?.message || 'Unknown error');
        this.isLoading = false;
      });
    }
  }

  /**
   * Cleans up the Dynamsoft Web TWAIN object when the component is destroyed.
   */
  ngOnDestroy() {
    if (this.DWTObject) {
      try {
        this.DWTObject.CloseSource();
        this.DWTObject = null;
      } catch (error) {
        console.error('Error cleaning up Dynamsoft:', error);
      }
    }
  }

  /**
   * Initializes the Dynamsoft Web TWAIN SDK for document scanning functionality
   * @returns Promise that resolves when Dynamsoft is successfully initialized
   */
  private async initDynamsoft(): Promise<void> {
    if (!Dynamsoft?.DWT) {
      throw new Error('Dynamsoft not loaded');
    }

    if (!environment.dynamsoft?.productKey) {
      throw new Error('Dynamsoft product key not configured');
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('Dynamsoft initialization timeout')),
        WebScanningDialogComponent.INIT_TIMEOUT
      );

      try {
        Dynamsoft.DWT.ProductKey = environment.dynamsoft.productKey;
        Dynamsoft.DWT.ResourcesPath = environment.dynamsoft.resourcesPath;

        Dynamsoft.DWT.Containers = [
          {
            ContainerId: 'dwtcontrolContainer',
            Width: '100%',
            Height: 'auto'
          }
        ];

        Dynamsoft.DWT.RegisterEvent('OnWebTwainReady', () => {
          try {
            clearTimeout(timeout);
            this.DWTObject = Dynamsoft.DWT.GetWebTwain('dwtcontrolContainer');

            if (!this.DWTObject) {
              reject(new Error('Failed to get WebTwain object'));
              return;
            }

            if (this.DWTObject?.Viewer) {
              this.DWTObject.Viewer.show();
              this.DWTObject.Viewer.setViewMode(
                WebScanningDialogComponent.DEFAULT_VIEW_MODE,
                WebScanningDialogComponent.DEFAULT_VIEW_MODE
              );
              this.DWTObject.Viewer.background = 'rgb(240, 242, 245)';
              this.DWTObject.Viewer.cursor = 'default';
              this.DWTObject.Viewer.allowPageDragging = true;
            }
            this.loadSources();
            resolve();
          } catch (error) {
            clearTimeout(timeout);
            reject(error);
          }
        });

        Dynamsoft.DWT.RegisterEvent('OnWebTwainNotFound', () => {
          clearTimeout(timeout);
          reject(new Error('Dynamsoft WebTwain service not found'));
        });

        Dynamsoft.DWT.Load();
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  /**
   * Loads available scanning sources (scanners/cameras) from the Dynamsoft Web TWAIN object
   */
  private loadSources() {
    if (!this.DWTObject) return;
    this.sources = [];

    for (let i = 0; i < this.DWTObject.SourceCount; i++) {
      this.sources.push(this.DWTObject.GetSourceNameItems(i));
    }

    if (this.sources.length > 0) {
      this.selectedSource = this.sources[0];
    }
    this.isLoading = false;
  }

  /**
   * Opens the selected scanners native UI and passes the local scan settings as defaults.
   */
  async useScannerUi() {
    if (!this.selectedSource || !this.DWTObject) return;

    const index = this.sources.indexOf(this.selectedSource);
    if (index < 0) return;

    await this.DWTObject.SelectSourceByIndexAsync(index);

    this.DWTObject.OpenSource();

    this.DWTObject.AcquireImage({
      IfShowUI: true,
      PixelType: this.scanSettings.colorDepth,
      Resolution: this.scanSettings.dpi,
      IfFeederEnabled: this.scanSettings.feederEnabled
    });
  }

  /**
   * Initiates the scanning process using the selected source device and Web Scanner settings
   */
  async scan() {
    if (!this.DWTObject || !this.selectedSource) return;

    const index = this.sources.indexOf(this.selectedSource);
    if (index < 0) return;

    try {
      await this.DWTObject.SelectSourceByIndexAsync(index);
      this.DWTObject.OpenSource();

      this.DWTObject.PixelType = this.scanSettings.colorDepth;
      this.DWTObject.Resolution = this.scanSettings.dpi;
      this.DWTObject.IfFeederEnabled = this.scanSettings.feederEnabled;

      this.DWTObject.AcquireImage({
        IfShowUI: false
      });
    } catch (error: any) {
      console.error('Scan failed:', error);
    }
  }

  /**
   * Emits the scanned pages as either one document or multiple to the file-uploader component.
   */
  processScannedFiles() {
    if (!this.DWTObject || this.DWTObject.HowManyImagesInBuffer === 0) return;

    const totalImages = this.DWTObject.HowManyImagesInBuffer;

    if (this.eachPageAsDocument) {
      this.processSinglePageDocuments(totalImages);
    } else if (this.splitAtBlankPage) {
      this.processBlankPageSplitting(totalImages);
    } else {
      this.processCombinedDocument(totalImages);
    }
  }

  /**
   * Processes each page as a separate document
   * @param totalImages The total number of scanned images to process.
   */
  private processSinglePageDocuments(totalImages: number) {
    const addedFiles: File[] = [];

    for (let i = 0; i < totalImages; i++) {
      const fileName = this.translate.instant('scannedPageFileName', {
        pageNumber: i + 1,
        timestamp: Date.now()
      });

      this.createPdfFile([i], fileName, file => {
        addedFiles.push(file);
        if (addedFiles.length === totalImages) {
          this.emitFilesAndClose(addedFiles);
        }
      });
    }
  }

  /**
   * Processes documents by splitting at blank pages
   * @param totalImages The total number of scanned images to process.
   */
  private processBlankPageSplitting(totalImages: number) {
    const segments = this.identifyBlankPageSegments(totalImages);
    const addedFiles: File[] = [];
    let processedSegments = 0;

    segments.forEach((indices, segIndex) => {
      const fileName = this.translate.instant('scannedDocFileName', {
        timestamp: Date.now(),
        partNumber: segIndex + 1
      });

      this.createPdfFile(indices, fileName, file => {
        addedFiles.push(file);
        processedSegments++;
        if (processedSegments === segments.length) {
          this.emitFilesAndClose(addedFiles);
        }
      });
    });
  }

  /**
   * Processes all pages as a single combined document
   * @param totalImages The total number of scanned images to process.
   */
  private processCombinedDocument(totalImages: number) {
    const fileName = this.translate.instant('scannedDocFileName', {
      timestamp: Date.now()
    });
    const indices = Array.from({ length: totalImages }, (_, i) => i);

    this.createPdfFile(indices, fileName, file => {
      this.emitFilesAndClose([file]);
    });
  }

  /**
   * Identifies segments of pages separated by blank pages
   * @param totalImages The total number of scanned images to process.
   */
  private identifyBlankPageSegments(totalImages: number): number[][] {
    const segments: number[][] = [[]];
    this.DWTObject.BlankImageMaxStdDev = WebScanningDialogComponent.BLANK_PAGE_THRESHOLD;

    for (let i = 0; i < totalImages; i++) {
      if (this.DWTObject.IsBlankImageExpress(i)) {
        if (segments[segments.length - 1].length > 0) {
          segments.push([]);
        }
      } else {
        segments[segments.length - 1].push(i);
      }
    }

    if (segments.length && segments[segments.length - 1].length === 0) {
      segments.pop();
    }

    return segments;
  }

  /**
   * Creates a PDF file from the specified page indices
   * @param indices The indices of the pages to include in the PDF file.
   * @param fileName The name of the resulting PDF file.
   * @param callback Callback function to handle the created PDF file.
   */
  private createPdfFile(indices: number[], fileName: string, callback: (file: File) => void) {
    this.DWTObject.ConvertToBlob(
      indices,
      Dynamsoft.DWT.EnumDWT_ImageType.IT_PDF,
      (result: Blob) => {
        const file = new File([result], fileName, { type: 'application/pdf' });
        callback(file);
      }
    );
  }

  /**
   * Emits the scanned files and closes the dialog.
   * @param files The array of scanned PDF files to emit.
   */
  private emitFilesAndClose(files: File[]) {
    this.filesScanned.emit(files);
    this.closeDialog();
  }

  /**
   * Closes the scanner dialog and emits visibility change event.
   */
  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
